import React from 'react';

import PropTypes from 'prop-types';

import {
    ButtonContainer,
    ContentContainer,
    ContentContainerColored,
    ImgContainer,
    TileBlock,
    TileColoredButton,
    TileColoredTitle,
    TileLink,
    TileWithImgTitle,
    TileWithoutBorderButtonContainer,
    TileWithoutBorderContentContainer,
    TileWithoutBorderImgContainer,
    TileWithoutBorderTitle,
} from './styles';
import GetStartedButton from '../get-started-button/GetStartedButton';
import { PromoButton } from '../promo-button/PromoButton';
import { PromoDangerouslyHtml } from '../promo-dangerously-html/PromoDangerouslyHtml';
import { PromoText } from '../promo-text/PromoText';
import { PromoDiskButton } from '../PromoDiskButton/PromoDiskButton';
import PromoIllustration from '../widgets/Illustration/PromoIllustration';

const getComponent = (props) => {
    if (props.url) {
        return TileLink;
    } else return TileBlock;
};

export const Tile = ({ bgColor = 'aubergine01', size = 'md', ...props }) => {
    const Container = getComponent(props);

    const isTileWithImg = props.variant === 'withImg' || props.variant === 'withImgAlt';
    const showButton = props.url || props.isExternalButton;

    return (
        <Container
            variant={props.variant}
            size={size}
            bgColor={bgColor}
            fullwidth={props.fullwidth}
            href={props.url}
            responsive={props.responsive}
            isCommonButton={props.isCommonButton}
        >
            {props.variant === 'colored' && (
                <>
                    <ContentContainerColored isCommonButton={props.isCommonButton}>
                        {props.title && (
                            <TileColoredTitle>
                                <PromoText tag="h3" variant="h1" color="white">
                                    {props.title}
                                </PromoText>
                            </TileColoredTitle>
                        )}
                        {props.subtitle && <PromoDangerouslyHtml tag="div" variant="subheader" color="white" dangerouslyHtml={props.subtitle} />}
                    </ContentContainerColored>
                    {showButton && (
                        <TileColoredButton isCommonButton={props.isCommonButton}>
                            {props.isExternalButton ? (
                                <GetStartedButton buttonText={props.btn_title} promoDarkPage />
                            ) : !props.isExternalButton && props.isCommonButton && props.btn_title ? (
                                <PromoButton caption={props.btn_title} variant="primary" isInverted type="pseudo" />
                            ) : (
                                <PromoDiskButton iconName="arrow-right" type="pseudo" variant="primary" size="large" isInverted />
                            )}
                        </TileColoredButton>
                    )}
                </>
            )}
            {isTileWithImg && (
                <>
                    {props.img && (
                        <ImgContainer
                            fullwidth={props.fullwidth}
                            responsive={props.responsive}
                            imgTop={props.imgTop}
                            withImgAlt={props.variant === 'withImgAlt'}
                        >
                            <PromoIllustration img={props.img} loadingLazy={props.loadingLazy} />
                        </ImgContainer>
                    )}
                    <ContentContainer fullwidth={props.fullwidth} withImgAlt={props.variant === 'withImgAlt'} imgTop={props.imgTop}>
                        {props.title && (
                            <TileWithImgTitle withImgAlt={props.variant === 'withImgAlt'}>
                                <PromoText tag="h3" variant="h3" color={props.titleColor} gradientColor={props.titleGradient}>
                                    {props.title}
                                </PromoText>
                            </TileWithImgTitle>
                        )}
                        {props.subtitle && (
                            <PromoDangerouslyHtml tag="div" variant="subheader" color={props.grayText ? 'gray40' : 'gray80'} dangerouslyHtml={props.subtitle} />
                        )}
                        {props.btn_title && (
                            <ButtonContainer>
                                {props.isExternalButton ? (
                                    <GetStartedButton buttonText={props.btn_title} />
                                ) : (
                                    <PromoButton caption={props.btn_title} variant="secondary" isInverted={props.isInvertedBtn} type="pseudo" />
                                )}
                            </ButtonContainer>
                        )}
                    </ContentContainer>
                </>
            )}
            {props.variant === 'withoutBorder' && props.img && (
                <>
                    {props.img && (
                        <TileWithoutBorderImgContainer fullwidth imgTop={props.imgTop}>
                            <PromoIllustration img={props.img} loadingLazy={props.loadingLazy} />
                        </TileWithoutBorderImgContainer>
                    )}
                    <TileWithoutBorderContentContainer>
                        {props.title && (
                            <TileWithoutBorderTitle>
                                <PromoText tag="h3" variant="h3" color={props.titleColor} gradientColor={props.titleGradient}>
                                    {props.title}
                                </PromoText>
                            </TileWithoutBorderTitle>
                        )}
                        {props.subtitle && (
                            <PromoDangerouslyHtml tag="div" variant="subheader" color={props.grayText ? 'gray40' : 'gray80'} dangerouslyHtml={props.subtitle} />
                        )}
                        {props.btn_title && (
                            <TileWithoutBorderButtonContainer>
                                <PromoButton caption={props.btn_title} variant="secondary" type="pseudo" />
                            </TileWithoutBorderButtonContainer>
                        )}
                    </TileWithoutBorderContentContainer>
                </>
            )}
        </Container>
    );
};

Tile.propTypes = {
    variant: PropTypes.oneOf(['colored', 'withImg', 'withImgAlt', 'withoutBorder']),
    size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
    bgColor: PropTypes.oneOf([
        'baseColor',
        'white',
        'primary',
        'primaryB2B',
        'purple80',
        'purple40',
        'purple20',
        'purple5',
        'gray80',
        'gray40',
        'gray20',
        'gray5',
        'aubergine01',
        'aubergine02',
        'aubergine03',
        'aubergine04',
        'eggshell01',
        'eggshell02',
        'eggshell03',
        'passionFruit01',
        'passionFruit02',
        'passionFruit03',
        'oceanBlue01',
        'oceanBlue02',
        'oceanBlue03',
        'blueberryBlue01',
        'blueberryBlue02',
        'blueberryBlue03',
        'jade01',
        'jade02',
        'jade03',
    ]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    titleColor: PropTypes.oneOf(['baseColor', 'white', 'primary', 'primaryB2B', 'gray80', 'gray40']),
    titleGradient: PropTypes.oneOf([
        'aubergine01',
        'aubergine02',
        'aubergine03',
        'aubergine04',
        'passionFruit01',
        'passionFruit02',
        'passionFruit03',
        'oceanBlue01',
        'oceanBlue02',
        'oceanBlue03',
        'blueberryBlue01',
        'blueberryBlue02',
        'blueberryBlue03',
        'jade01',
        'jade02',
        'jade03',
    ]),
    img: PropTypes.array,
    imgTop: PropTypes.bool,
    fullwidth: PropTypes.bool,
    btn_title: PropTypes.string,
    url: PropTypes.string,
    grayText: PropTypes.bool,
    withoutBorder: PropTypes.bool,
    responsive: PropTypes.bool,
    loadingLazy: PropTypes.bool,
    isExternalButton: PropTypes.bool,
};
